import { useEffect, useState, type ReactNode } from 'react';
import { ArrowLeftRight, ArrowDownToLine, ArrowUpFromLine, ShoppingCart } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import {
  fetchTransfers,
  fetchEntrees,
  fetchSorties,
  fetchVentes,
  deleteTransfer,
  deleteEntree,
  deleteSortie,
  deleteVente,
} from '@/store/thunks/movementThunks';
import type { MovementRecord } from '@/store/slices/movementSlice';
import { filterListByQuery } from '@/utils/filterList';
import Page from '@/components/Page';
import { Tabs, TabPanel, DataTable, ListWithPagination, Modal, AddButton, FilterInput, ConfirmModal, TableActions } from '@/components/ui';
import TransferForm from '@/components/movement/TransferForm';
import EntreeForm from '@/components/movement/EntreeForm';
import SortieForm from '@/components/movement/SortieForm';
import VenteForm from '@/components/movement/VenteForm';

const dateRender = (v: unknown) => (v ? new Date(v as string).toLocaleDateString('fr-FR') : '—');

function citerneInfo(v: unknown): ReactNode {
  if (v == null || typeof v !== 'object') return '—';
  const o = v as { code?: string; capacite?: number; current_value?: number };
  const code = o.code ?? '—';
  const parts = [code];
  if (o.capacite != null) parts.push(`Cap. ${o.capacite}`);
  if (o.current_value != null) parts.push(`Stock ${o.current_value}`);
  return <span className="whitespace-nowrap">{parts.join(' · ')}</span>;
}

function tierInfo(v: unknown): ReactNode {
  if (v == null || typeof v !== 'object') return '—';
  const o = v as { code?: string; nom?: string; type?: string };
  const name = o.nom ?? o.code ?? '—';
  const extra = [o.code && o.code !== name ? o.code : null, o.type].filter(Boolean);
  return <span className="whitespace-nowrap">{extra.length ? `${name} (${extra.join(' · ')})` : name}</span>;
}

function engineInfo(v: unknown): ReactNode {
  if (v == null || typeof v !== 'object') return '—';
  const o = v as { code?: string; type?: string; unite?: string };
  const code = o.code ?? '—';
  const extra = [o.type, o.unite].filter(Boolean);
  return <span className="whitespace-nowrap">{extra.length ? `${code} (${extra.join(' · ')})` : code}</span>;
}

function chantierInfo(v: unknown): ReactNode {
  if (v == null || typeof v !== 'object') return '—';
  const o = v as { code?: string; nom?: string };
  return o.nom ?? o.code ?? '—';
}

export default function MovmentPage() {
  const dispatch = useAppDispatch();
  const { transfer, entree, sortie, vente } = useAppSelector((s: RootState) => s.movement);
  const [tab, setTab] = useState(0);

  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [entreeModalOpen, setEntreeModalOpen] = useState(false);
  const [sortieModalOpen, setSortieModalOpen] = useState(false);
  const [venteModalOpen, setVenteModalOpen] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState<MovementRecord | null>(null);
  const [editingEntree, setEditingEntree] = useState<MovementRecord | null>(null);
  const [editingSortie, setEditingSortie] = useState<MovementRecord | null>(null);
  const [editingVente, setEditingVente] = useState<MovementRecord | null>(null);

  const [confirmDelete, setConfirmDelete] = useState<{ kind: 'transfer' | 'entree' | 'sortie' | 'vente'; id: number } | null>(null);
  const [filterTransfer, setFilterTransfer] = useState('');
  const [filterEntree, setFilterEntree] = useState('');
  const [filterSortie, setFilterSortie] = useState('');
  const [filterVente, setFilterVente] = useState('');

  useEffect(() => {
    if (tab === 0) dispatch(fetchTransfers(0));
    else if (tab === 1) dispatch(fetchEntrees(0));
    else if (tab === 2) dispatch(fetchSorties(0));
    else if (tab === 3) dispatch(fetchVentes(0));
  }, [tab, dispatch]);

  const transferColumns = [
    { key: 'id', label: 'Id' },
    { key: 'date', label: 'Date', render: dateRender },
    { key: 'quantite', label: 'Quantité' },
    { key: 'citerne_emetteur', label: 'Citerne émetteur', render: (_: unknown, row: Record<string, unknown>) => citerneInfo(row.citerne_emetteur) },
    { key: 'citerne_destinataire', label: 'Citerne destinataire', render: (_: unknown, row: Record<string, unknown>) => citerneInfo(row.citerne_destinataire) },
  ];
  const entreeColumns = [
    { key: 'id', label: 'Id' },
    { key: 'dateAchat', label: 'Date', render: dateRender },
    { key: 'quantite', label: 'Quantité' },
    { key: 'prixAchat', label: 'Prix' },
    { key: 'citerne', label: 'Citerne', render: (_: unknown, row: Record<string, unknown>) => citerneInfo(row.citerne) },
    { key: 'tier', label: 'Fournisseur', render: (_: unknown, row: Record<string, unknown>) => tierInfo(row.tier) },
  ];
  const sortieColumns = [
    { key: 'id', label: 'Id' },
    { key: 'date', label: 'Date', render: dateRender },
    { key: 'quantite', label: 'Quantité' },
    { key: 'compteur', label: 'Compteur' },
    { key: 'chantier', label: 'Chantier', render: (_: unknown, row: Record<string, unknown>) => chantierInfo(row.chantier) },
    { key: 'citerne', label: 'Citerne', render: (_: unknown, row: Record<string, unknown>) => citerneInfo(row.citerne) },
    { key: 'engine', label: 'Engin', render: (_: unknown, row: Record<string, unknown>) => engineInfo(row.engine) },
  ];
  const venteColumns = [
    { key: 'id', label: 'Id' },
    { key: 'date', label: 'Date', render: dateRender },
    { key: 'quantite', label: 'Quantité' },
    { key: 'prix', label: 'Prix' },
    { key: 'citerne', label: 'Citerne', render: (_: unknown, row: Record<string, unknown>) => citerneInfo(row.citerne) },
    { key: 'tier', label: 'Client', render: (_: unknown, row: Record<string, unknown>) => tierInfo(row.tier) },
  ];

  const transferDataRaw = transfer.list as Record<string, unknown>[];
  const entreeDataRaw = entree.list as Record<string, unknown>[];
  const sortieDataRaw = sortie.list as Record<string, unknown>[];
  const venteDataRaw = vente.list as Record<string, unknown>[];
  const transferData = filterListByQuery(transferDataRaw, filterTransfer, ['id', 'quantite', 'date']);
  const entreeData = filterListByQuery(entreeDataRaw, filterEntree, ['id', 'quantite', 'dateAchat', 'prixAchat']);
  const sortieData = filterListByQuery(sortieDataRaw, filterSortie, ['id', 'quantite', 'date', 'compteur']);
  const venteData = filterListByQuery(venteDataRaw, filterVente, ['id', 'quantite', 'date', 'prix']);

  const handleDeleteConfirm = () => {
    if (!confirmDelete) return;
    const { kind, id } = confirmDelete;
    if (kind === 'transfer') dispatch(deleteTransfer(id));
    else if (kind === 'entree') dispatch(deleteEntree(id));
    else if (kind === 'sortie') dispatch(deleteSortie(id));
    else if (kind === 'vente') dispatch(deleteVente(id));
    setConfirmDelete(null);
  };

  const renderActions = (row: Record<string, unknown>, kind: 'transfer' | 'entree' | 'sortie' | 'vente') => {
    const id = row.id as number | undefined;
    if (id == null) return null;
    const openEdit = () => {
      const record = row as MovementRecord;
      if (kind === 'transfer') { setEditingTransfer(record); setTransferModalOpen(true); }
      else if (kind === 'entree') { setEditingEntree(record); setEntreeModalOpen(true); }
      else if (kind === 'sortie') { setEditingSortie(record); setSortieModalOpen(true); }
      else { setEditingVente(record); setVenteModalOpen(true); }
    };
    return (
      <TableActions
        onEdit={openEdit}
        onDelete={() => setConfirmDelete({ kind, id })}
      />
    );
  };

  return (
    <Page title="Mouvements">
      <Tabs
        tabs={[
          { label: 'Transfer', icon: <ArrowLeftRight className="h-4 w-4" /> },
          { label: 'Entrées', icon: <ArrowDownToLine className="h-4 w-4" /> },
          { label: 'Sortie', icon: <ArrowUpFromLine className="h-4 w-4" /> },
          { label: 'Vente', icon: <ShoppingCart className="h-4 w-4" /> },
        ]}
        value={tab}
        onChange={setTab}
      />

      <TabPanel value={tab} index={0}>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <FilterInput value={filterTransfer} onChange={setFilterTransfer} placeholder="Rechercher…" id="filter-transfer" />
            <AddButton label="Ajouter" onClick={() => { setEditingTransfer(null); setTransferModalOpen(true); }} />
          </div>
          <ListWithPagination page={transfer.pageNum} totalPages={transfer.totalPages} onPageChange={(p) => dispatch(fetchTransfers(p))}>
            <DataTable<Record<string, unknown>>
              columns={transferColumns}
              data={transferData}
              keyField="id"
              emptyMessage="Aucun transfert"
              actions={(row) => renderActions(row, 'transfer')}
            />
          </ListWithPagination>
        </div>
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <FilterInput value={filterEntree} onChange={setFilterEntree} placeholder="Rechercher…" id="filter-entree" />
            <AddButton label="Ajouter" onClick={() => { setEditingEntree(null); setEntreeModalOpen(true); }} />
          </div>
          <ListWithPagination page={entree.pageNum} totalPages={entree.totalPages} onPageChange={(p) => dispatch(fetchEntrees(p))}>
            <DataTable<Record<string, unknown>>
              columns={entreeColumns}
              data={entreeData}
              keyField="id"
              emptyMessage="Aucune entrée"
              actions={(row) => renderActions(row, 'entree')}
            />
          </ListWithPagination>
        </div>
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <FilterInput value={filterSortie} onChange={setFilterSortie} placeholder="Rechercher…" id="filter-sortie" />
            <AddButton label="Ajouter" onClick={() => { setEditingSortie(null); setSortieModalOpen(true); }} />
          </div>
          <ListWithPagination page={sortie.pageNum} totalPages={sortie.totalPages} onPageChange={(p) => dispatch(fetchSorties(p))}>
            <DataTable<Record<string, unknown>>
              columns={sortieColumns}
              data={sortieData}
              keyField="id"
              emptyMessage="Aucune sortie"
              actions={(row) => renderActions(row, 'sortie')}
            />
          </ListWithPagination>
        </div>
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <FilterInput value={filterVente} onChange={setFilterVente} placeholder="Rechercher…" id="filter-vente" />
            <AddButton label="Ajouter" onClick={() => { setEditingVente(null); setVenteModalOpen(true); }} />
          </div>
          <ListWithPagination page={vente.pageNum} totalPages={vente.totalPages} onPageChange={(p) => dispatch(fetchVentes(p))}>
            <DataTable<Record<string, unknown>>
              columns={venteColumns}
              data={venteData}
              keyField="id"
              emptyMessage="Aucune vente"
              actions={(row) => renderActions(row, 'vente')}
            />
          </ListWithPagination>
        </div>
      </TabPanel>

      <Modal size='xl' open={transferModalOpen} onClose={() => setTransferModalOpen(false)} title={editingTransfer ? 'Modifier le transfert' : 'Nouveau transfert'}>
        <TransferForm transfer={editingTransfer} type={editingTransfer ? 'update' : 'create'} onClose={() => setTransferModalOpen(false)} onSuccess={() => setTransferModalOpen(false)} />
      </Modal>
      <Modal size='xl' open={entreeModalOpen} onClose={() => setEntreeModalOpen(false)} title={editingEntree ? "Modifier l'entrée" : "Nouvelle entrée"}>
        <EntreeForm entree={editingEntree} type={editingEntree ? 'update' : 'create'} onClose={() => setEntreeModalOpen(false)} onSuccess={() => setEntreeModalOpen(false)} />
      </Modal>
      <Modal size='xl' open={sortieModalOpen} onClose={() => setSortieModalOpen(false)} title={editingSortie ? 'Modifier la sortie' : 'Nouvelle sortie'}>
        <SortieForm sortie={editingSortie} type={editingSortie ? 'update' : 'create'} onClose={() => setSortieModalOpen(false)} onSuccess={() => setSortieModalOpen(false)} />
      </Modal>
      <Modal size='xl' open={venteModalOpen} onClose={() => setVenteModalOpen(false)} title={editingVente ? 'Modifier la vente' : 'Nouvelle vente'}>
        <VenteForm vente={editingVente} type={editingVente ? 'update' : 'create'} onClose={() => setVenteModalOpen(false)} onSuccess={() => setVenteModalOpen(false)} />
      </Modal>

      <ConfirmModal
        open={!!confirmDelete}
        title="Confirmer la suppression"
        message="Voulez-vous vraiment supprimer cet enregistrement ?"
        onConfirm={handleDeleteConfirm}
        onClose={() => setConfirmDelete(null)}
      />
    </Page>
  );
}
